import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  getUserSettingsCulture,
  getUserSettingsCurrency,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { LocalizationService } from '@capital-access/common/localization';
import { getSelectedSecurity, getSelectedSecuritySurveillancePermission } from '@capital-access/common/security';
import { NotificationService } from '@capital-access/firefly/components';
import { ReportType } from '@capital-access/reporting/common';
import { createConfigurableItineraryReport } from '@capital-access/reporting/common';
import { createConfigurableItineraryReportFinished } from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { generateReport } from '../../core/+state/report.actions';
import { OwnershipSource } from '../../core/models/ownership-source.enum';
import { ConfigurableItineraryReportRequest } from '../models/configurable-itinerary-report.models';
import { FileNameService } from '../../core/services/file-name.service';

@Injectable()
export class ConfigurableItineraryReportEffects {
  createConfigurableItineraryReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createConfigurableItineraryReport),
      withLatestFrom(
        this.store.select(getSelectedSecurity),
        this.store.select(getSelectedSecuritySurveillancePermission),
        this.store.select(getUserSettingsCulture),
        this.store.select(getUserSettingsLanguage),
        this.store.select(getUserSettingsCurrency),
        this.store.select(getUserSettingsTimezone)
      ),
      mergeMap(([request, security, isSrvPermissioned, culture, language, currency, timeZone]) => {
        const incoming = request as any;
        const isSrv = security?.surveillance ?? false;
        const baseRequest: ConfigurableItineraryReportRequest = {
          ...request,
          culture,
          language,
          currency,
          reportType: ReportType.Itinerary,
          pullSampleData: false,
          fileName: '',
          timeZone: incoming?.timeZone ?? timeZone,
          dataSource: isSrv ? OwnershipSource.Surveillance : OwnershipSource.Public,
          securityId: incoming?.securityId ?? security?.id
        } as ConfigurableItineraryReportRequest;

        const eventName = incoming?.eventName ?? '';

        return this.fileNameService.getDefaultFileName(isSrvPermissioned, isSrv, eventName, baseRequest.timeZone).pipe(
          map(fileName => {
            const req = { ...baseRequest, fileName } as ConfigurableItineraryReportRequest;
            return this.generateItineraryReportRequest(req);
          }),
          switchMap(generatedRequest =>
            this.localization.localize('exportStarted', {}, { scope: LOCALIZATION_KEY }).pipe(
              tap(message => this.notificationService.notify(message, { type: 'info' })),
              map(() => generatedRequest)
            )
          )
        );
      })
    )
  );

  private generateItineraryReportRequest(request: ConfigurableItineraryReportRequest, isExportLocalization = false) {
    const successMessage = 'itineraryReportCreated';
    const failureMessage = 'itineraryReportFailed';

    return generateReport({
      request,
      url: 'configurable-reporting/process/itinerary/launch',
      successMessage,
      failureMessage,
      finishAction: createConfigurableItineraryReportFinished()
    });
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private localization: LocalizationService,
    private notificationService: NotificationService,
    private fileNameService: FileNameService
  ) {}
}
